import { useState, useEffect } from 'react';
import { Platform } from 'react-native';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { onUnreadChanged } from '../services/chat/unreadSignal';
import { chatPresenceHub } from '../services/chat/presenceHub';
import {
  createUnreadStore,
  ZERO_UNREAD,
  REFRESH_MS,
  type ChatApiUnreadCounts,
} from '../services/chat/unreadStore';

export type { ChatApiUnreadCounts } from '../services/chat/unreadStore';
export { REFRESH_MS };

/**
 * The app-wide unread source. One poll, one listener set, one in-flight
 * request — see `services/chat/unreadStore.ts` for why it is shared and not
 * per-hook (CORE-1102).
 */
const store = createUnreadStore({
  fetchCounts: () => api.getChatUnreadSummary(),
  onUnreadChanged: (listener) => onUnreadChanged(listener),
  onMessage: (listener) => chatPresenceHub.addMessageListener(listener),
  // CORE-1753 — тот же хаб на вебе и в приложении: вход, переподключение и
  // возврат из фона перечитывают счётчики, а не ждут опроса.
  onLiveFeedUp: (listener) => chatPresenceHub.addConnectedListener(listener),
  onFocus:
    Platform.OS === 'web' && typeof window !== 'undefined'
      ? (listener) => {
        window.addEventListener('focus', listener);
        return () => window.removeEventListener('focus', listener);
      }
      : undefined,
});

/**
 * CORE-617 (toward CORE-377) — header + bottom-tab unread badges sourced from
 * Agape.ChatApi (`GET /api/chat/channels/unread-summary`) instead of the
 * GetStream client's `queryChannels`/`countUnread` aggregation. The server
 * categorizes friends / conversations / total from read pointers and excludes
 * blocked-peer DMs, so the app no longer needs a live Stream connection just to
 * show a badge.
 *
 * CORE-1102 — the timer, the listeners and the fetch moved into a shared store.
 * This hook is now only «render whatever the store holds»: mounting a second
 * badge costs a `Set.add`, not another 30 s poll. Returns ZERO when signed out.
 */
export function useChatApiUnreadCounts(): ChatApiUnreadCounts {
  const { isAuthenticated, isLoading } = useAuth();
  const active = isAuthenticated && !isLoading;
  const [counts, setCounts] = useState<ChatApiUnreadCounts>(
    () => (active ? store.getCounts() : ZERO_UNREAD),
  );

  useEffect(() => {
    if (!active) {
      // Signed out (or still deciding) — blank the badge and hold no poll.
      store.reset();
      setCounts(ZERO_UNREAD);
      return;
    }
    // Seed synchronously from whatever the store already knows, so a badge
    // mounting second doesn't flash a zero while the first one's poll is
    // mid-flight.
    setCounts(store.getCounts());
    return store.subscribe(setCounts);
  }, [active]);

  return counts;
}
